import { useEffect, useRef, useState } from "react";
import React from "react";
import { toast } from "react-toastify";

import { AiProcessingContent } from "components/modals/Files-categorization-modal/AiProcessedContent";
import { Approve } from "components/Prompts/Approve";
import { useActionHook, usePrompt } from "hooks";
import { useDealActions, useDealIdSelector, useUserActions } from "store";
import { DealBase, ICategorizedFile, IEmail, ISuggestion } from "store/types";

import { EmailPreviewContent } from "../Components/EmailPreviewContent";
import { MultipleFilesCategorize } from "../Components/MultipleFilesCategorize";
import { ToastContent } from "../Components/ToastContent";
import { EmailInboxFilter } from "../EmailInbox";
import { IHandleOpenPreviewProps, IPagination, onMoveEmailData } from "./Email-context";

export const useEmailContextHook = ({ isFullScreenMode }: { isFullScreenMode: boolean }) => {
  const { getEmailsAction, moveEmailAction, deleteEmailAction } = useUserActions();
  const { getDealChunksAction } = useDealActions();
  const currentDealId = useDealIdSelector();
  const openPrompt = usePrompt();
  const [emailPreviewId, setEmailPreviewId] = useState<number | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(isFullScreenMode || false);
  const [selectedDeal, setSelectedDeal] = useState<DealBase | null>(null);
  const [selectedEmails, setSelectedEmails] = useState<IEmail[]>([]);
  const [emailsData, setEmailsData] = useState<IEmail[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<EmailInboxFilter>({
    deal: [],
    association: null,
    attachment: null
  });
  const [pagination, setPagination] = useState<IPagination>({
    page: 1,
    per_page: 30,
    total: 0
  });

  const { loading, action } = useActionHook(getEmailsAction, {
    onSuccess: (res) => {
      setEmailsData(res.data.data);
      setPagination((prev) => ({ ...prev, total: res.data.total }));
    }
  });

  const { action: moveEmail } = useActionHook(moveEmailAction);

  // ----------- multiple emails actions -----------

  //move multiple emails to deal (use in case of multiple emails selection)
  // - if selected emails have attachments, open prompt to categorize files
  // - if selected emails don't have attachments, move emails to selected deal
  const handleMultipleMove = async () => {
    if (!selectedDeal) return;
    if (selectedEmails.some((email) => email.attachments.length)) {
      const result = await openPrompt({
        Component: MultipleFilesCategorize,
        props: {
          selectedEmails: selectedEmails,
          initialSelectedDeal: selectedDeal
        }
      });

      if (result) {
        await Promise.all(
          selectedEmails.map(async (email) => {
            const emailCategorizedFiles = result.filesValues
              .filter((file) => file.investor_id && file.type && file.email_id === email.id)
              .map((file) => {
                return { file_id: file.file_id, type: file.type, investor_id: file.investor_id };
              });
            return moveEmail(email, {
              deal_id: result.selectedDeal.id,
              ...(emailCategorizedFiles.length && { categorized_files: emailCategorizedFiles as ICategorizedFile[] })
            });
          })
        );
        toast(<ToastContent message={selectedEmails.length + " emails"} moveTo={`${selectedDeal.title}`} />, {
          className: "email-toastify",
          autoClose: 7000
        });
        setEmailsData((prev) =>
          prev.map((email) => {
            if (selectedEmails.some((eml) => eml.id === email.id)) {
              return { ...email, deal_id: selectedDeal.id };
            }
            return email;
          })
        );
        setSelectedEmails([]);
        setSelectedDeal(null);
      }
      return;
    }

    await Promise.all(
      selectedEmails
        .map((eml) => {
          const email = emailsData.find((email) => email.id === eml.id);
          return email ? moveEmail(email, { deal_id: selectedDeal.id }) : null;
        })
        .filter((promise) => promise !== null)
    );

    if (currentDealId && currentDealId === selectedDeal.id) {
      getDealChunksAction(selectedDeal.id);
    }
    toast(<ToastContent message={selectedEmails.length + " emails"} moveTo={`${selectedDeal.title}`} />, {
      className: "email-toastify",
      autoClose: 7000
    });

    setEmailsData((prev) =>
      prev.map((email) => {
        if (selectedEmails.some((eml) => eml.id === email.id)) {
          return { ...email, deal_id: selectedDeal.id };
        }
        return email;
      })
    );
    setSelectedEmails([]);
    setSelectedDeal(null);
  };

  const onMoveWithAiExtraction = (email: IEmail, selectedDeal: DealBase) => {
    openPrompt({
      Component: AiProcessingContent,
      props: {
        title: "AI Extraction",
        type: "aiCategorization",
        applyButtonText: "Continue",
        cb: async () => {
          selectedDeal &&
            (await onMove({
              email,
              deal: selectedDeal,
              extract_with_ai: true
            }));
        }
      }
    });
  };

  //select email (list item checkbox)
  const onSelectEmail = (email: IEmail) => {
    if (selectedEmails.some((e) => e.id === email.id)) {
      setSelectedEmails((prev) => prev.filter((e) => e.id !== email.id));
    } else {
      setSelectedEmails((prev) => [...prev, email]);
    }
  };

  //delete multiple emails (use in case of multiple emails selection)
  const handleMultipleDelete = async () => {
    await Promise.all(
      selectedEmails.map((email) => {
        return deleteEmailAction(email.id);
      })
    );
    setEmailsData((prev) => prev.filter((email) => !selectedEmails.some((eml) => eml.id === email.id)));
    setSelectedEmails([]);
  };

  //cancel select deal (use in case of multiple emails selection)
  const onCancelSelectDeal = () => {
    setSelectedDeal(null);
    setSelectedEmails([]);
  };

  // ----------- single email actions -----------

  //move email to deal (single email)
  const onMove = async (data: onMoveEmailData) => {
    const { email, deal, categorizedFiles, extract_with_ai, is_reassign } = data;
    await moveEmail(email, {
      deal_id: deal.id,
      //if categorizedFile type is null, file will moved without categorization
      categorized_files: categorizedFiles?.filter((file) => file.type),
      extract_with_ai,
      is_reassign
    });
    setEmailsData((prev) =>
      prev.map((emailFromData) => {
        if (emailFromData.id === email.id) {
          return {
            ...emailFromData,
            deal_id: deal.id
          };
        }
        return emailFromData;
      })
    );

    if (currentDealId && currentDealId === deal.id) {
      await getDealChunksAction(deal.id);
    }
    toast(<ToastContent message={"Email: " + email.email_subject || ""} moveTo={`${deal.title}`} />, {
      className: "email-toastify",
      autoClose: 7000
    });
  };

  //delete single email
  const handleDelete = async (emailId: number) => {
    await deleteEmailAction(emailId);
    setEmailsData((prev) => prev.filter((email) => email.id !== emailId));
  };

  //open email preview (use in case of single email selection)
  // - if full screen mode is enabled, set emailPreviewId for opened preview on the right side
  // - if full screen mode is disabled, open prompt with email preview content
  const handleOpenPreview = async (props: IHandleOpenPreviewProps) => {
    const { email, isReAssign } = props;
    if (isFullScreen && !isReAssign) {
      return setEmailPreviewId(email.id);
    }
    const result = await openPrompt({
      Component: EmailPreviewContent,
      props: {
        email,
        isReAssign
      }
    });
    if (result) {
      let shouldMove = true;

      if (isReAssign) {
        const approveResult = await openPrompt({
          Component: Approve,
          props: {
            title: "Are you sure you want to reassign this email?",
            description: "Email will be moved to the new deal, including all attachments."
          }
        });
        shouldMove = !!approveResult;
      }

      if (shouldMove) {
        await onMove({
          email: result.email,
          deal: result.deal,
          categorizedFiles: result.categorizedFiles,
          is_reassign: isReAssign
        });
      }
    }
  };

  //set suggestion deal for email
  const setSuggestionDeal = (suggestion: ISuggestion, emailId: number) => {
    setEmailsData((prev) =>
      prev.map((email) => {
        if (email.id === emailId) {
          return {
            ...email,
            suggestions: [suggestion, ...(email.suggestions?.filter((s) => s.id !== suggestion.id) || [])]
          };
        }
        return email;
      })
    );
  };

  // ----------- sort and filter actions -----------

  const onSortChange = (sort: "desc" | "asc") => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSort(sort);
  };

  const onFilterChange = (filter: EmailInboxFilter) => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setFilter(filter);
  };

  //get emails
  useEffect(() => {
    action({
      page: pagination.page,
      ...(searchValue && { search: searchValue }),
      ...(filter.association && { is_associated: filter.association === "Associated only" ? 1 : 0 }),
      order_by: sort,
      ...(filter.attachment && { with_attachment: filter.attachment === "With attachment" ? 1 : 0 }),
      deal_ids: filter.deal?.map((deal) => deal.id) || [],
      per_page: 30
    });
  }, [pagination.page, searchValue, filter, sort]);

  //if emailsData is changed, set emailPreviewId to the first email id
  useEffect(() => {
    setEmailPreviewId(emailsData.length ? emailsData[0].id : null);
  }, [emailsData[0]?.id]);

  return {
    emailsData,
    loading,
    scrollContainerRef,
    isFullScreen,
    setIsFullScreen,
    searchValue,
    setSearchValue,
    filter,
    setFilter,
    pagination,
    setPagination,
    selectedEmails,
    setSelectedEmails,
    selectedDeal,
    setSelectedDeal,
    onMove,
    handleMultipleMove,
    handleMultipleDelete,
    handleDelete,
    onCancelSelectDeal,
    onSelectEmail,
    onSortChange,
    onFilterChange,
    sort,
    handleOpenPreview,
    emailPreviewId,
    setSuggestionDeal,
    onMoveWithAiExtraction
  };
};
